import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import {
  useOpportunity,
  useUpdateOpportunity,
  useCustomers,
} from "@/hooks/use-api";
import {
  opportunityFields,
  opportunityToFormValues,
  type OpportunityFormValues,
} from "./opportunity-form-config";

export default function V2OpportunityEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const opportunityQuery = useOpportunity(id);
  const updateOpportunity = useUpdateOpportunity();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: OpportunityFormValues) {
    setError(null);
    try {
      await updateOpportunity.mutateAsync({
        id,
        input: values as Record<string, unknown>,
      });
      toast.success("Opportunity updated");
      navigate(`/opportunities/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  const opportunity = (
    opportunityQuery.data as
      | { opportunity?: Record<string, unknown> }
      | undefined
  )?.opportunity;

  // The customer select is required — gate on the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (opportunityQuery.isLoading || customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the opportunity form can't be edited yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  if (opportunityQuery.isError || !opportunity) {
    return (
      <EmptyState
        title="Opportunity not found"
        description="This opportunity may have been removed, or you don't have access to it."
        action={{
          label: "Back to opportunities",
          onClick: () => navigate("/opportunities"),
        }}
      />
    );
  }

  return (
    <EntityForm<OpportunityFormValues>
      title="Edit opportunity"
      breadcrumbs={[
        { label: "Opportunities", href: "/opportunities" },
        { label: "Edit" },
      ]}
      defaultValues={opportunityToFormValues(opportunity)}
      fields={opportunityFields(customerOptions)}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref={`/opportunities/${id}`}
      error={error}
      isSubmitting={updateOpportunity.isPending}
    />
  );
}
